import { Controller } from "@hotwired/stimulus"

import LoaderController from "../loader_controller"

const CRITERION_SCORE_ATTR = "data-criterion-score"

interface Review {
  customerName: string
  description: string
  badgeClasses: string[]
  ratingValue: number
  ratingDescription: string
  reviewedDate: string
  formattedReviewedDate: string
}

export default class extends Controller {
  static outlets = ["page-loader"]
  declare readonly pageLoaderOutlet: LoaderController

  static targets = [
    "criterion",
    "filterDropdown",
    "filterOption",
    "hotelReview",
    "hotelReviewsContainer",
    "hotelShowMoreButton",
    "hotelTab",
    "reviewTemplate",
    "spaReview",
    "spaReviewsContainer",
    "spaShowMoreButton",
    "spaTab",
    "tab"
  ]

  declare readonly criterionTargets: HTMLElement[]
  declare readonly filterDropdownTarget: HTMLElement
  declare readonly filterOptionTargets: HTMLInputElement[]
  declare readonly hotelReviewTargets: HTMLDivElement[]
  declare readonly hotelReviewsContainerTarget: HTMLDivElement
  declare readonly hotelShowMoreButtonTarget: HTMLButtonElement
  declare readonly hotelTabTarget: HTMLDetailsElement
  declare readonly reviewTemplateTarget: HTMLTemplateElement
  declare readonly spaReviewTargets: HTMLDivElement[]
  declare readonly spaReviewsContainerTarget: HTMLDivElement
  declare readonly spaShowMoreButtonTarget: HTMLButtonElement
  declare readonly spaTabTarget: HTMLDetailsElement
  declare readonly tabTargets: HTMLDetailsElement[]

  declare readonly hasHotelTabTarget: boolean
  declare readonly hasSpaTabTarget: boolean

  declare readonly hasSpaShowMoreButtonTarget: boolean
  declare readonly hasHotelShowMoreButtonTarget: boolean

  static values = {
    availableFilters: Object,
    batchSize: Number,
    reviews: Object
  }

  declare readonly availableFiltersValue: { spa: number[]; hotel: number[] }
  declare readonly batchSizeValue: number
  declare reviewsValue: { spa: Review[]; hotel: Review[] }

  declare filteredReviews: { spa: Review[]; hotel: Review[] }
  declare maxVisibleResults: { spa: number; hotel: number }

  initialize(): void {
    this.filteredReviews = this.reviewsValue
    this.maxVisibleResults = { spa: this.batchSizeValue, hotel: this.batchSizeValue }
  }

  connect(): void {
    if (window.location.hash === "#reviews") {
      this.element.scrollIntoView({ block: "start" })
    }

    this.animateReviewsCriteria()
    this.updateFilterAvailability()
  }

  updateFilterAvailability(): void {
    const { hotel, spa } = this.availableFiltersValue
    const availableFilters = this.isSpaActiveTab ? spa : hotel

    this.filterOptionTargets.forEach((option: HTMLInputElement) => {
      const enabled = availableFilters.includes(parseInt(option.value))
      option.disabled = !enabled

      if (option.checked && !enabled) {
        this.resetFilters()
      }
    })
  }

  filterReviews({ currentTarget }: Event): void {
    const value = parseInt((currentTarget as HTMLInputElement).value)

    if (value === 0) {
      this.resetFilters()
      return
    }

    this.filterDropdownTarget.classList.add("--selected")

    this.updateReviews()
    this.updateShowButtonsVisibility()
  }

  resetFilters(): void {
    this.filterDropdownTarget.classList.remove("--selected")

    this.filterOptionTargets[0].checked = true

    this.updateReviews()
    this.updateShowButtonsVisibility()
  }

  showMore(): void {
    const total = this.reviewsData.length
    const newVisibleCount = Math.min(this.visibleReviewsCount + this.batchSizeValue, total)

    this.renderReviews(this.reviewsData, this.activeTab, newVisibleCount, this.visibleReviewsCount)

    if (this.isSpaActiveTab) {
      this.maxVisibleResults.spa = newVisibleCount
    } else {
      this.maxVisibleResults.hotel = newVisibleCount
    }

    this.updateShowButtonsVisibility()
  }

  sortReviews({ currentTarget }: Event): void {
    const value = (currentTarget as HTMLInputElement).value

    this.sortResult(value)
    this.updateReviews()
  }

  private animateReviewsCriteria(): void {
    const observer = new window.IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement
          const score = target.getAttribute(CRITERION_SCORE_ATTR)!

          target.style.setProperty("transform", `translateX(${+score * 10}%)`, "important")
          observer.unobserve(target)
        }
      })
    })

    this.criterionTargets.forEach((item) => observer.observe(item))
  }

  private sortResult(value: string): void {
    const { hotel, spa } = this.reviewsValue
    const sortFn =
      value === "recent" ? this.sortByDate : this.sortByRating.bind(this, value === "rating_desc")

    this.reviewsValue = {
      spa: spa.toSorted(sortFn),
      hotel: hotel.toSorted(sortFn)
    }
  }

  private generateReview(review: Review, tabType: string): DocumentFragment {
    const clone = this.reviewTemplateTarget.content.cloneNode(true) as DocumentFragment
    clone.firstElementChild!.setAttribute("data-reviews-target", tabType)

    const paragraphs = clone.querySelectorAll<HTMLParagraphElement>("p")

    paragraphs[0].textContent = review.customerName
    paragraphs[1].textContent = review.formattedReviewedDate
    paragraphs[2].textContent = review.description

    const ratingValue = clone.querySelector("div span:first-child") as HTMLSpanElement
    const ratingDescription = clone.querySelector("div span:last-child") as HTMLSpanElement

    ratingValue.classList.add(...review.badgeClasses)
    ratingValue.textContent = review.ratingValue.toString()
    ratingDescription.textContent = review.ratingDescription

    return clone
  }

  private renderReviews(
    reviews: Review[],
    tab: HTMLDetailsElement,
    endIndex: number,
    startIndex: number = 0
  ): void {
    const isSpaTab = this.hasSpaTabTarget && tab === this.spaTabTarget
    const container = isSpaTab ? this.spaReviewsContainerTarget : this.hotelReviewsContainerTarget
    const tabType = isSpaTab ? "spaReview" : "hotelReview"

    if (startIndex === 0) {
      container.innerHTML = ""
    }

    for (let index = startIndex; index < endIndex; index += 1) {
      const review = reviews[index]
      const reviewElement = this.generateReview(review, tabType)

      container.append(reviewElement)
    }
  }

  private updateReviews(): void {
    // Simulate request to provide visual feedback
    this.pageLoaderOutlet.start()
    window.setTimeout(() => this.pageLoaderOutlet.stop(), 250)

    const selectedRating = +this.filterOptionTargets.find((option) => option.checked)!.value
    const filterFn = (review: Review): boolean => review.ratingValue >= selectedRating

    this.filteredReviews = {
      spa: this.reviewsValue.spa.filter(filterFn),
      hotel: this.reviewsValue.hotel.filter(filterFn)
    }

    const { spa, hotel } = this.filteredReviews

    if (this.hasSpaTabTarget) {
      const visibleCount = Math.min(this.maxVisibleResults.spa, spa.length)
      this.renderReviews(spa, this.spaTabTarget, visibleCount)
    }

    if (this.hasHotelTabTarget) {
      const visibleCount = Math.min(this.maxVisibleResults.hotel, hotel.length)
      this.renderReviews(hotel, this.hotelTabTarget, visibleCount)
    }
  }

  private sortByDate(a: Review, b: Review): number {
    const dateA = a.reviewedDate
    const dateB = b.reviewedDate
    return dateA === dateB ? 0 : dateA < dateB ? 1 : -1
  }

  private sortByRating(descending: boolean, a: Review, b: Review): number {
    if (descending) {
      return b.ratingValue - a.ratingValue
    } else {
      return a.ratingValue - b.ratingValue
    }
  }

  private updateShowButtonsVisibility(): void {
    const { hotel, spa } = this.maxVisibleResults

    if (this.hasHotelShowMoreButtonTarget) {
      const isVisible = this.filteredReviews.hotel.length > hotel
      this.hotelShowMoreButtonTarget.classList.toggle("hidden", !isVisible)
    }

    if (this.hasSpaShowMoreButtonTarget) {
      const isVisible = this.filteredReviews.spa.length > spa
      this.spaShowMoreButtonTarget.classList.toggle("hidden", !isVisible)
    }
  }

  private get activeTab(): HTMLDetailsElement {
    return this.tabTargets.find((tab) => tab.open) ?? this.tabTargets[0]
  }

  private get isSpaActiveTab(): boolean {
    return this.hasSpaTabTarget && this.activeTab === this.spaTabTarget
  }

  private get reviewsData(): Review[] {
    if (this.isSpaActiveTab) {
      return this.filteredReviews.spa!
    } else {
      return this.filteredReviews.hotel!
    }
  }

  private get visibleReviewsCount(): number {
    if (this.isSpaActiveTab) {
      return this.spaReviewTargets.length
    } else {
      return this.hotelReviewTargets.length
    }
  }
}
